import { z } from "zod";

/**
 * Canonical list of industry tags. The Insights page renders industries in
 * this order. Industries not in the list are still surfaced as "Untagged"
 * counts but are not aggregated into a row.
 */
export const INDUSTRIES = [
  "NFP",
  "Community Services",
  "Hospitality",
  "Government",
  "Council",
  "Retail",
  "Fast Food",
  "Health",
  "Mental Health",
  "Aged Care",
  "Education",
  "Transport / Logistics",
  "Construction / Mining",
  "Utilities / Resources",
  "Security",
  "Corrections",
  "Financial Services",
  "Insurance",
] as const;
export type Industry = (typeof INDUSTRIES)[number];

export const METRIC_KEYS = [
  "extended_rate",
  "network_adoption",
  "productive_hours",
  "sessions",
  "emergency_activations",
  "checkin_rate",
] as const;
export type MetricKey = (typeof METRIC_KEYS)[number];

export const METRIC_META: Record<
  MetricKey,
  { label: string; lowerIsBetter: boolean; unit: "pct" | "hours" | "int" }
> = {
  extended_rate: { label: "Extended Rate", lowerIsBetter: true, unit: "pct" },
  network_adoption: { label: "Network Adoption", lowerIsBetter: false, unit: "pct" },
  productive_hours: { label: "Productive Hours", lowerIsBetter: false, unit: "hours" },
  sessions: { label: "Sessions", lowerIsBetter: false, unit: "int" },
  emergency_activations: { label: "Emergency Activations", lowerIsBetter: true, unit: "int" },
  checkin_rate: { label: "Check-in Rate", lowerIsBetter: false, unit: "pct" },
};

export const MetricStatsSchema = z.object({
  avg: z.number(),
  median: z.number(),
  min: z.number(),
  max: z.number(),
  count: z.number().int().nonnegative(),
});
export type MetricStats = z.infer<typeof MetricStatsSchema>;

export const IndustryClientEntrySchema = z.object({
  clientId: z.string().uuid(),
  clientName: z.string(),
  slug: z.string(),
  avgExtendedRate: z.number().nullable(),
  avgNetworkAdoption: z.number().nullable(),
  avgProductiveHours: z.number().nullable(),
  avgSessions: z.number().nullable(),
  avgEmergencyActivations: z.number().nullable(),
  avgCheckinRate: z.number().nullable(),
});
export type IndustryClientEntry = z.infer<typeof IndustryClientEntrySchema>;

const MetricsBucketSchema = z.object({
  extended_rate: MetricStatsSchema.nullable(),
  network_adoption: MetricStatsSchema.nullable(),
  productive_hours: MetricStatsSchema.nullable(),
  sessions: MetricStatsSchema.nullable(),
  emergency_activations: MetricStatsSchema.nullable(),
  checkin_rate: MetricStatsSchema.nullable(),
  // Not all clients use check-in, so it lives in the Insights page's "more
  // stats" disclosure rather than the headline tiles -- alongside these two,
  // which were never headline material. Deliberately not MetricKeys: they
  // don't feed peer benchmarking (benchmarkForClient), only this bucket.
  total_hours: MetricStatsSchema.nullable(),
  team_alerts: MetricStatsSchema.nullable(),
});

export const IndustryRowSchema = z.object({
  industry: z.string(),
  clientCount: z.number().int().nonnegative(),
  totalArr: z.number().int().nonnegative().nullable(),
  // Devices across this industry's clients not (yet) marked decommissioned,
  // as of `deviceCountAsOfMonth` on the response. A client tagged in two
  // industries contributes its device count to both, same as totalArr.
  activeDeviceCount: z.number().int().nonnegative(),
  clients: z.array(IndustryClientEntrySchema),
  metrics: MetricsBucketSchema,
});
export type IndustryRow = z.infer<typeof IndustryRowSchema>;

export const IndustriesResponseSchema = z.object({
  industries: z.array(IndustryRowSchema),
  availableMonths: z.array(z.string()),
  untaggedClientCount: z.number().int().nonnegative(),
  // The (year, month) activeDeviceCount was computed as of — the selected
  // month filter, or the latest known month when viewing "All time". Null
  // only when no reporting period exists anywhere yet.
  deviceCountAsOfMonth: z.string().nullable(),
});
export type IndustriesResponse = z.infer<typeof IndustriesResponseSchema>;

const PeerComparisonSchema = z.object({
  client: z.number().nullable(),
  peerAvg: z.number(),
  peerMedian: z.number(),
  peerMin: z.number(),
  peerMax: z.number(),
  peerCount: z.number().int().nonnegative(),
  rank: z.number().int().positive().nullable(),
  total: z.number().int().positive(),
  lowerIsBetter: z.boolean(),
});
export type PeerComparison = z.infer<typeof PeerComparisonSchema>;

export const BenchmarkResponseSchema = z.object({
  clientId: z.string().uuid(),
  clientName: z.string(),
  industries: z.array(z.string()),
  peerCount: z.number().int().nonnegative(),
  availableMonths: z.array(z.string()),
  metrics: z.object({
    extended_rate: PeerComparisonSchema.nullable(),
    network_adoption: PeerComparisonSchema.nullable(),
    productive_hours: PeerComparisonSchema.nullable(),
    sessions: PeerComparisonSchema.nullable(),
    emergency_activations: PeerComparisonSchema.nullable(),
    checkin_rate: PeerComparisonSchema.nullable(),
    // Derived: emergency_activations normalised per 1,000 sessions. Not one
    // of METRIC_KEYS — it's a size-neutral ratio computed only for this
    // client/peer comparison, not tracked as a standalone metric elsewhere.
    emergency_rate: PeerComparisonSchema.nullable(),
  }),
});
export type BenchmarkResponse = z.infer<typeof BenchmarkResponseSchema>;
